import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../utils/auth';
import {
  RELATION_OPTIONS,
  isBlockingRelation,
  SELF_RELATION,
} from '../../config/relationConstants';

/**
 * SubjectConfirmStep — Tier-2 confirm-facts step (PR: person-picker).
 *
 * Renders ONLY when the subject is a chat-added person (sibling/adult), NOT the
 * onboarded child. It collects, FROM THE USER:
 *   - relation (dropdown + free-text fallback for "annen")
 *   - age OR birth year
 *   - one or more diagnoses
 * and persists them to subjectIdentity on the draft.
 *
 * Memory is shown READ-ONLY as a reference ("Coord husker dette om {navn}:")
 * so the user can read and transcribe. It is NEVER parsed into the inputs and
 * NEVER fed into the generated prose — see the PR design: no LLM parsing of
 * memory into fields, no free-text memory injected into generation.
 *
 * The inputs ARE pre-populated from a previously-confirmed subjectIdentity
 * (so resume/edit works), but never from memory.
 */

// RELATION_OPTIONS is single-sourced in config/relationConstants.js (shared with
// AddFamilyMemberModal's adult/child entity_type mapping so the two never drift).

// The fixed relation values; anything else is treated as a custom ("annen")
// free-text relation.
const FIXED_RELATIONS = RELATION_OPTIONS.filter(o => o.value !== 'annen').map(o => o.value);

export function SubjectConfirmStep({
  step,
  subject,
  onConfirmSubjectFacts,
  onBlockedChange,
  onBackToPicker,
}) {
  const identity = subject?.subjectIdentity || null;
  const name = subject?.subjectName || 'personen';
  const entityId = subject?.subjectEntityId || null;

  // ── Local form state, seeded from any previously-confirmed identity ────────
  // Relation: if a stored relation isn't one of the fixed options, show the
  // dropdown on "annen" and put the value in the free-text box.
  const storedRelation = identity?.relation || '';
  const isStoredCustom = storedRelation && !FIXED_RELATIONS.includes(storedRelation);

  const [relationSelect, setRelationSelect] = useState(
    isStoredCustom ? 'annen' : storedRelation
  );
  const [relationOther, setRelationOther] = useState(isStoredCustom ? storedRelation : '');
  const [age, setAge] = useState(identity?.age != null ? String(identity.age) : '');
  const [birthYear, setBirthYear] = useState(
    identity?.birthYear != null ? String(identity.birthYear) : ''
  );
  const [diagnoses, setDiagnoses] = useState(
    identity?.diagnoses?.length ? [...identity.diagnoses] : ['']
  );

  // ── Advance gate ───────────────────────────────────────────────────────────
  // The picked subject is one the user can't file a barn-søknad for: either an
  // adult relation, or "Dette er meg" (the user themselves — the soft residual
  // that Layer-1 entityId self-exclusion couldn't catch). Block advancing and
  // route back to the picker. Uses relationSelect (the dropdown value) so an
  // adult/self choice blocks immediately, before any "annen" free-text.
  const blocked = isBlockingRelation(relationSelect);
  const blockedBySelf = relationSelect === SELF_RELATION;

  // Report the blocked state up so the container can disable "Neste". Fires on
  // mount and whenever it flips; clears on unmount so leaving the step (e.g. the
  // wizard skipping it for a Tier-1 child) never leaves a stale block.
  useEffect(() => {
    onBlockedChange?.(blocked);
    return () => onBlockedChange?.(false);
  }, [blocked, onBlockedChange]);

  // ── Read-only memory reference ─────────────────────────────────────────────
  const [memories, setMemories] = useState([]);
  const [memoriesLoading, setMemoriesLoading] = useState(false);

  const fetchMemories = useCallback(async () => {
    if (!entityId) {
      setMemories([]);
      return;
    }
    setMemoriesLoading(true);
    try {
      // READ-ONLY per-entity fetch. Param is `entity` (maps to entity_id on the
      // backend). No translate=true (avoids the known redundant-translation
      // perf hazard). This NEVER writes and is NEVER parsed into the inputs.
      const res = await authFetch(
        `/memories?category=family_members&entity=${encodeURIComponent(entityId)}`
      );
      if (res.ok) {
        const data = await res.json();
        setMemories(Array.isArray(data.memories) ? data.memories : []);
      }
    } catch (err) {
      console.error('[SubjectConfirmStep] memory reference fetch failed:', err);
    } finally {
      setMemoriesLoading(false);
    }
  }, [entityId]);

  useEffect(() => {
    fetchMemories();
  }, [fetchMemories]);

  // ── Persist on change. Builds the canonical subjectIdentity and hands it up;
  // the container debounces the actual PUT (and flushes on navigation), so this
  // can fire freely on every keystroke. ─────────────────────────────────────
  const persist = useCallback((next) => {
    const relation = next.relationSelect === 'annen'
      ? (next.relationOther.trim() || null)
      : (next.relationSelect || null);

    const parsedAge = next.age.trim() !== '' ? Number(next.age) : null;
    const parsedBirthYear = next.birthYear.trim() !== '' ? Number(next.birthYear) : null;
    const cleanedDiagnoses = next.diagnoses
      .map(d => d.trim())
      .filter(Boolean);

    onConfirmSubjectFacts({
      relation,
      age: Number.isFinite(parsedAge) ? parsedAge : null,
      birthYear: Number.isFinite(parsedBirthYear) ? parsedBirthYear : null,
      diagnoses: cleanedDiagnoses,
    });
  }, [onConfirmSubjectFacts]);

  // Snapshot of current field values for persist().
  const snapshot = (overrides = {}) => ({
    relationSelect, relationOther, age, birthYear, diagnoses, ...overrides,
  });

  const handleRelationSelect = (e) => {
    const value = e.target.value;
    setRelationSelect(value);
    persist(snapshot({ relationSelect: value }));
  };
  const handleRelationOther = (e) => {
    const value = e.target.value;
    setRelationOther(value);
    persist(snapshot({ relationOther: value }));
  };
  const handleAge = (e) => {
    const value = e.target.value;
    setAge(value);
    persist(snapshot({ age: value }));
  };
  const handleBirthYear = (e) => {
    const value = e.target.value;
    setBirthYear(value);
    persist(snapshot({ birthYear: value }));
  };

  const updateDiagnosis = (idx, value) => {
    const nextDiag = diagnoses.map((d, i) => (i === idx ? value : d));
    setDiagnoses(nextDiag);
    persist(snapshot({ diagnoses: nextDiag }));
  };
  const addDiagnosis = () => {
    const nextDiag = [...diagnoses, ''];
    setDiagnoses(nextDiag);
  };
  const removeDiagnosis = (idx) => {
    const nextDiag = diagnoses.filter((_, i) => i !== idx);
    const finalDiag = nextDiag.length ? nextDiag : [''];
    setDiagnoses(finalDiag);
    persist(snapshot({ diagnoses: finalDiag }));
  };

  const inputClass =
    'w-full px-4 py-3 rounded-xl border border-border-light bg-background-card ' +
    'text-text-primary focus:outline-none focus:border-sage-400 focus:ring-1 focus:ring-sage-300';

  return (
    <div>
      <h2 className="text-2xl font-semibold text-text-primary mb-2">
        {step.title}
      </h2>
      <p className="text-text-secondary mb-6 leading-relaxed">
        {step.instruction?.replace('{name}', name)}
      </p>

      {/* Read-only memory reference */}
      {entityId && (memoriesLoading || memories.length > 0) && (
        <div className="mb-6 bg-sage-50 border border-sage-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-sage-800 mb-2">
            Coord husker dette om {name}:
          </h3>
          {memoriesLoading ? (
            <p className="text-sm text-sage-700">Henter …</p>
          ) : (
            <ul className="space-y-1.5">
              {memories.map((m) => (
                <li key={m.id} className="text-sm text-sage-700 flex gap-2">
                  <span className="text-sage-400 flex-shrink-0">•</span>
                  <span>{m.content}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-sage-600 mt-3 italic">
            Dette er bare til hjelp. Fyll inn feltene under selv – vi bruker kun
            det du skriver her i søknaden.
          </p>
        </div>
      )}

      <div className="space-y-6">
        {/* Relation */}
        <div>
          <label className="block text-base font-medium text-text-primary mb-2">
            Hva er {name} for deg?
          </label>
          <select value={relationSelect} onChange={handleRelationSelect} className={inputClass}>
            <option value="">Velg relasjon …</option>
            {RELATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {relationSelect === 'annen' && (
            <input
              type="text"
              value={relationOther}
              onChange={handleRelationOther}
              placeholder="Beskriv relasjonen (f.eks. nevø, samboers barn)"
              className={`${inputClass} mt-3`}
            />
          )}
        </div>

        {/* Advance gate message (adult relation or "Dette er meg"). Blocks the
            wizard and offers a one-tap way back to the picker. */}
        {blocked && (
          <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
            <p className="text-text-primary">
              {blockedBySelf
                ? 'Du kan ikke søke hjelpestønad for deg selv her. Velg barnet søknaden gjelder.'
                : 'Coord hjelper foreløpig med søknader for barn. Velg barnet søknaden gjelder.'}
            </p>
            <button
              type="button"
              onClick={() => onBackToPicker?.()}
              className="
                mt-3 inline-flex items-center gap-2
                px-5 py-2.5 rounded-xl
                bg-primary text-white font-semibold text-sm
                hover:bg-primary-600 transition-colors
              "
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Tilbake til valg av person
            </button>
          </div>
        )}

        {/* Age / birth year + diagnoses — hidden when blocked (no point
            collecting facts for a subject the user can't apply for). */}
        {!blocked && (
        <>
        <div>
          <label className="block text-base font-medium text-text-primary mb-2">
            Hvor gammel er {name}?
          </label>
          <div className="flex gap-3">
            <div className="flex-1">
              <input
                type="number"
                inputMode="numeric"
                value={age}
                onChange={handleAge}
                placeholder="Alder"
                min="0"
                max="120"
                className={inputClass}
              />
            </div>
            <div className="flex items-center text-text-muted text-sm">eller</div>
            <div className="flex-1">
              <input
                type="number"
                inputMode="numeric"
                value={birthYear}
                onChange={handleBirthYear}
                placeholder="Fødselsår"
                min="1900"
                className={inputClass}
              />
            </div>
          </div>
        </div>

        {/* Diagnoses */}
        <div>
          <label className="block text-base font-medium text-text-primary mb-2">
            Diagnose(r)
          </label>
          <div className="space-y-2">
            {diagnoses.map((d, idx) => (
              <div key={idx} className="flex gap-2">
                <input
                  type="text"
                  value={d}
                  onChange={(e) => updateDiagnosis(idx, e.target.value)}
                  placeholder="f.eks. ADHD, Autisme"
                  className={inputClass}
                />
                {diagnoses.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeDiagnosis(idx)}
                    className="px-3 rounded-xl text-text-muted hover:text-error-600 hover:bg-error-50 transition-colors"
                    aria-label="Fjern diagnose"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addDiagnosis}
            className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary-600 font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Legg til diagnose
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

export default SubjectConfirmStep;
